export const PRESENCE_LABELS = {
  online: 'En ligne',
  idle: 'Inactif',
  offline: 'Hors ligne',
  unknown: 'Présence inconnue',
}

export const ACCOUNT_STATUS_LABELS = {
  active: 'Actif',
  unconfirmed: 'Non confirmé',
  disabled: 'Désactivé',
}

export const ROLE_LABELS = {
  admin: 'Admin',
  user: 'Membre',
}

export function isAdminDisplay(user) {
  return Boolean(user?.is_admin)
}

export function adminRoleState(user, { loading = false, profileError = null } = {}) {
  if (loading) return 'loading'
  if (profileError) return 'profile_error'
  if (!user) return 'unauthenticated'
  if (!Object.prototype.hasOwnProperty.call(user, 'is_admin')) return 'loading'
  return user.is_admin ? 'admin' : 'not_admin'
}

export function presenceLabel(status) {
  return PRESENCE_LABELS[status] || PRESENCE_LABELS.unknown
}

export function presenceHint(status, freshnessSeconds) {
  if (!status || status === 'unknown' || !PRESENCE_LABELS[status]) {
    return 'Aucun signal de présence n’a été enregistré. Cela ne signifie pas que le compte est hors ligne.'
  }
  return freshnessLabel(freshnessSeconds)
}

export function roleLabel(role) {
  if (ROLE_LABELS[role]) return ROLE_LABELS[role]
  const value = String(role || '').trim()
  return value || 'Rôle inconnu'
}

export function accountStatusLabel(status) {
  return ACCOUNT_STATUS_LABELS[status] || 'État du compte inconnu'
}

export function adminDisplayName(user) {
  const display = String(user?.display_name || '').trim()
  if (display) return display
  const username = String(user?.username || '').trim()
  if (username) return username
  const email = String(user?.email || '').trim()
  if (email) {
    const local = email.split('@')[0].trim()
    return local || email
  }
  const id = String(user?.id || '').trim()
  if (id) return `Profil ${id.slice(0, 8)}`
  return 'Identifiant indisponible'
}

export function adminInitials(user) {
  const name = adminDisplayName(user)
  const parts = name.split(/[\s._-]+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

export function formatStamp(value, emptyLabel = '—') {
  if (!value) return emptyLabel
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return emptyLabel
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date)
}

export function freshnessLabel(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) {
    return 'Aucune donnée de présence'
  }
  const value = Math.max(0, Number(seconds))
  if (value < 60) return `Signal il y a ${Math.round(value)} s`
  if (value < 3600) return `Signal il y a ${Math.round(value / 60)} min`
  return `Signal il y a ${Math.round(value / 3600)} h`
}

export function missingOrError(value, hadError) {
  if (hadError) return 'Erreur de chargement'
  if (value == null) return '—'
  return value
}

export function buildAdminUserParams({ query = '', role = 'all', presence = 'all', page = 1, pageSize = 20 } = {}) {
  const params = { page, page_size: pageSize }
  if (query.trim()) params.q = query.trim()
  if (role !== 'all') params.role = role
  if (presence !== 'all') params.presence = presence
  return params
}

export function articlesCountLabel(count, hadError) {
  if (hadError) return 'Nombre d’articles indisponible'
  if (count == null || count === '') return 'Nombre d’articles indisponible'
  const value = Number(count)
  if (!Number.isFinite(value)) return 'Nombre d’articles indisponible'
  return value === 1 ? '1 article' : `${value} articles`
}

export function resetOutcomeText(notice) {
  if (!notice) return ''
  const message = String(notice.message || '').trim()
  if (notice.accepted && notice.delivered) {
    return [message, 'Demande acceptée et livraison indiquée par le service.'].filter(Boolean).join(' ')
  }
  if (notice.accepted) {
    return [message, 'Demande acceptée par le service. La livraison effective du courriel n’est pas confirmée.'].filter(Boolean).join(' ')
  }
  return message || 'La demande n’a pas été acceptée.'
}

export function adminPagerState({ page = 1, total = 0, pageSize = 20, hasError = false } = {}) {
  const size = Math.max(1, Number(pageSize) || 20)
  const n = hasError ? 0 : Math.max(0, Number(total) || 0)
  const pages = n === 0 ? 1 : Math.ceil(n / size)
  const current = Math.min(Math.max(1, Number(page) || 1), pages)
  return {
    page: current,
    pages,
    prevDisabled: Boolean(hasError) || current <= 1,
    nextDisabled: Boolean(hasError) || n === 0 || current >= pages,
    label: `${current} / ${pages}`,
  }
}
